// Game-over / victory screen. Shows the run score; if it makes the top 20 (and a session
// is available), congratulates the player and lets them submit a name + country flag (the
// score is auto-filled and read-only). After submitting, shows their rank in the board.

import { COUNTRIES, flagEmoji } from '../net/leaderboardClient';
import type { LeaderboardClient } from '../net/leaderboardClient';
import { renderLeaderboard } from './leaderboardView';
import { AVATAR_COUNT, avatarCanvas } from '../render/pixelArt';
import { shareScore } from './shareCard';
import type { ShareData } from './shareCard';
import { adaptiveHint, A_BTN } from './hint';
import { looksXbox, setInputMode } from '../core/inputMode';
import { isCompactTouchLayout } from './platform';

export interface GameOverStats {
  score: number;
  kills: number;
  level: number;
  seconds: number;
  victory: boolean;
  ranked: boolean; // easy-mode runs are not submitted to the global leaderboard
}

const FONT = '15px "Silkscreen", ui-monospace, monospace';

export class GameOverUI {
  private readonly overlay: HTMLDivElement;
  private readonly celebration: HTMLDivElement;
  private readonly box: HTMLDivElement;
  private shareData: ShareData | null = null;
  private readonly onRestart: () => void;
  private readonly onExit?: () => void;
  private readonly onNativeShare?: (data: ShareData) => void;
  private focusables: HTMLButtonElement[] = [];
  private focusIndex = 0;
  private padRaf = 0;
  private readonly prevPad = { left: false, right: false, a: false };
  private readonly compact = isCompactTouchLayout();

  constructor(mount: HTMLElement, onRestart: () => void, onExit?: () => void, onNativeShare?: (data: ShareData) => void) {
    this.onRestart = onRestart;
    this.onExit = onExit;
    this.onNativeShare = onNativeShare;
    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'absolute',
      inset: '0',
      display: 'none',
      placeItems: 'center',
      background: 'rgba(8,6,14,0.82)',
      zIndex: '30',
      pointerEvents: 'auto',
      userSelect: 'none',
      font: FONT,
      color: '#e8e4f0',
    });
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.celebration = document.createElement('div');
    Object.assign(this.celebration.style, {
      position: 'absolute',
      inset: '0',
      display: 'none',
      overflow: 'hidden',
      pointerEvents: 'none',
    });
    this.box = document.createElement('div');
    Object.assign(this.box.style, {
      width: 'min(460px, 92vw)',
      maxHeight: '88vh',
      overflowY: 'auto',
      textAlign: 'center',
      padding: '24px',
      position: 'relative',
      zIndex: '1',
    });
    ensureVictoryStyles();
    this.overlay.append(this.celebration, this.box);
    mount.appendChild(this.overlay);
  }

  get visible(): boolean {
    return this.overlay.style.display !== 'none';
  }

  hide(): void {
    this.overlay.style.display = 'none';
    this.celebration.style.display = 'none';
    this.celebration.replaceChildren();
    this.box.replaceChildren();
    this.focusables = [];
    window.removeEventListener('keydown', this.onKey);
    if (this.padRaf !== 0) {
      cancelAnimationFrame(this.padRaf);
      this.padRaf = 0;
    }
  }

  show(stats: GameOverStats, client: LeaderboardClient): void {
    this.box.replaceChildren();
    const mm = Math.floor(stats.seconds / 60).toString().padStart(2, '0');
    const ss = Math.floor(stats.seconds % 60).toString().padStart(2, '0');

    this.setVictoryPresentation(stats.victory);
    if (stats.victory) {
      const eyebrow = line('RUN COMPLETE', '#5cc8ff', this.compact ? '11px' : '12px');
      eyebrow.style.letterSpacing = '2px';
      this.box.append(eyebrow, victoryCrest());
      const title = heading('VICTORY', '#ffd34d', this.compact ? '30px' : '34px');
      title.className = 'brewmancer-victory-title';
      this.box.appendChild(title);
      this.box.appendChild(line('THE HORDE HAS FALLEN', '#9fffa0', this.compact ? '11px' : '12px'));
      this.box.appendChild(victoryStats(`${mm}:${ss}`, stats.kills, stats.level, this.compact));
    } else {
      this.box.appendChild(heading('GAME OVER', '#ff8a98'));
      this.box.appendChild(line(`${mm}:${ss}  ·  ${stats.kills} kills  ·  level ${stats.level}`, '#cdc7dd', '13px'));
    }

    const scoreEl = document.createElement('div');
    scoreEl.textContent = stats.score.toLocaleString();
    Object.assign(scoreEl.style, {
      font: `${stats.victory && !this.compact ? 40 : 34}px "Silkscreen", ui-monospace, monospace`,
      color: '#ffd34d',
      margin: stats.victory ? '5px 0 4px' : '14px 0 4px',
      minHeight: stats.victory ? '48px' : '41px',
      textShadow: stats.victory ? '0 3px 0 #6b4a08' : 'none',
    });
    this.box.appendChild(line(stats.victory ? 'FINAL SCORE' : 'SCORE', '#9a93b0', '11px'));
    this.box.appendChild(scoreEl);

    const slot = document.createElement('div'); // filled async with form / board
    slot.style.margin = '16px 0';
    this.box.appendChild(slot);

    // Action buttons: Share + Restart, navigable by mouse, keyboard arrows, and gamepad.
    this.shareData = { score: stats.score, kills: stats.kills, seconds: stats.seconds, level: stats.level, victory: stats.victory };
    const shareBtn = actionButton(this.compact ? 'SHARE SCORE' : '↗ SHARE SCORE', '#6a4fd0', 'rgba(60,40,110,0.55)', '#fff');
    shareBtn.onclick = (): void => {
      if (this.shareData === null) return;
      if (this.onNativeShare !== undefined) this.onNativeShare(this.shareData);
      else void shareScore(this.shareData);
    };
    const restartBtn = actionButton(this.compact ? 'RESTART' : '▶ RESTART', '#6b4a08', 'linear-gradient(#ffd24a, #f0a81e)', '#2a1a00');
    restartBtn.onclick = (): void => {
      restartBtn.disabled = true;
      restartBtn.style.opacity = '0.65';
      this.onRestart();
    };

    const menuBtn = this.onExit ? actionButton('MAIN MENU', '#4a4366', 'rgba(20,16,30,0.82)', '#e8e4f0') : null;
    if (menuBtn !== null) menuBtn.onclick = (): void => this.onExit?.();

    const actions = document.createElement('div');
    Object.assign(actions.style, { display: 'flex', gap: '10px', justifyContent: 'center', margin: '4px 0 8px', flexWrap: 'wrap' });
    actions.append(shareBtn, restartBtn);
    if (menuBtn !== null) actions.appendChild(menuBtn);
    this.box.appendChild(actions);
    if (!this.compact) {
      const navHint = adaptiveHint('arrows move · Enter select', `D-pad move · ${A_BTN} select`);
      Object.assign(navHint.style, { color: '#7a7390', fontSize: '11px', marginTop: '2px' });
      this.box.appendChild(navHint);
    }

    // Focus starts on Restart (tap A / Enter to retry); arrows or d-pad move to Share.
    this.focusables = menuBtn === null ? [shareBtn, restartBtn] : [shareBtn, restartBtn, menuBtn];
    this.focusIndex = 1;
    this.prevPad.left = true;
    this.prevPad.right = true;
    this.prevPad.a = true;
    this.paintFocus();
    window.addEventListener('keydown', this.onKey);
    this.startPadPoll();

    this.overlay.style.display = 'grid';
    void this.populate(slot, stats, client);
  }

  private setVictoryPresentation(victory: boolean): void {
    this.overlay.setAttribute('aria-label', victory ? 'Victory. Run complete.' : 'Game over.');
    this.overlay.style.background = victory ? 'rgba(5,5,14,0.9)' : 'rgba(8,6,14,0.82)';
    this.box.style.width = victory ? 'min(560px, calc(100vw - 72px))' : 'min(460px, 92vw)';
    this.box.style.padding = this.compact ? '14px 18px' : '24px';
    this.box.style.borderTop = victory ? '2px solid #ffd34d' : 'none';
    this.box.style.borderBottom = victory ? '2px solid #5cc8ff' : 'none';
    this.box.style.background = victory ? 'rgba(10,8,18,0.72)' : 'transparent';
    this.celebration.replaceChildren();
    this.celebration.style.display = victory ? 'block' : 'none';
    if (!victory) return;

    const colors = ['#ffd34d', '#5cc8ff', '#ff4de1', '#9fffa0', '#a78bfa'];
    for (let i = 0; i < 42; i++) {
      const pixel = document.createElement('span');
      pixel.className = 'brewmancer-victory-pixel';
      const angle = ((i * 137.5) * Math.PI) / 180;
      const distance = 110 + (i % 7) * 18;
      const size = 4 + (i % 3) * 2;
      Object.assign(pixel.style, {
        left: `${47 + ((i * 17) % 7)}%`,
        top: `${43 + ((i * 11) % 9)}%`,
        width: `${size}px`,
        height: `${size}px`,
        background: colors[i % colors.length],
        animationDelay: `${(i % 8) * 35}ms`,
        animationDuration: `${1050 + (i % 6) * 100}ms`,
      });
      pixel.style.setProperty('--victory-x', `${Math.cos(angle) * distance}px`);
      pixel.style.setProperty('--victory-y', `${Math.sin(angle) * distance}px`);
      this.celebration.appendChild(pixel);
    }
  }

  private async populate(slot: HTMLDivElement, stats: GameOverStats, client: LeaderboardClient): Promise<void> {
    if (client.usesNativeLeaderboard) {
      slot.style.display = 'none';
      return;
    }

    // Easy mode is just for fun - don't submit to (or pollute) the global leaderboard.
    if (!stats.ranked) {
      slot.appendChild(line('Easy mode - just for fun, not ranked on the leaderboard. 🎉', '#9fdcff', '12px'));
      const board = renderLeaderboard((await client.fetchLeaderboard()).slice(0, 10));
      board.style.marginTop = '12px';
      slot.appendChild(board);
      return;
    }
    const entries = await client.fetchLeaderboard();
    const qualifies = entries.length < 20 || stats.score > entries[entries.length - 1].score;
    if (qualifies && client.hasSession()) {
      this.buildSubmitForm(slot, stats, client);
    } else {
      if (client.hasSession() || !this.compact) {
        slot.appendChild(line(client.hasSession() ? 'Not in the top 20 this time.' : 'Leaderboard offline.', '#9a93b0', '12px'));
      }
      if (entries.length > 0) {
        const board = renderLeaderboard(entries.slice(0, 10));
        board.style.marginTop = '12px';
        slot.appendChild(board);
      } else if (!client.hasSession() && this.compact) {
        slot.style.display = 'none';
      }
    }
  }

  private buildSubmitForm(slot: HTMLDivElement, stats: GameOverStats, client: LeaderboardClient): void {
    slot.appendChild(heading('🏆 TOP 20!', '#ffd34d', '20px'));
    slot.appendChild(line('Submit your name to the global leaderboard:', '#cdc7dd', '12px'));

    // Optional avatar picker (default = the wizard).
    let avatar = 0;
    slot.appendChild(line('Pick an avatar:', '#9a93b0', '11px'));
    const avatarRow = document.createElement('div');
    Object.assign(avatarRow.style, { display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center', margin: '8px 0 4px' });
    const boxes: HTMLDivElement[] = [];
    for (let i = 0; i < AVATAR_COUNT; i++) {
      const b = document.createElement('div');
      Object.assign(b.style, { padding: '2px', border: '2px solid transparent', borderRadius: '5px', cursor: 'pointer', lineHeight: '0' });
      b.appendChild(avatarCanvas(i, 3));
      b.onclick = (): void => {
        avatar = i;
        boxes.forEach((x, j) => (x.style.borderColor = j === i ? '#5cc8ff' : 'transparent'));
      };
      boxes.push(b);
      avatarRow.appendChild(b);
    }
    boxes[0].style.borderColor = '#5cc8ff';
    slot.appendChild(avatarRow);

    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', gap: '8px', justifyContent: 'center', margin: '12px 0', flexWrap: 'wrap' });

    const name = document.createElement('input');
    name.maxLength = 24;
    name.placeholder = 'Your name';
    Object.assign(name.style, {
      font: FONT,
      padding: '8px 10px',
      width: '170px',
      border: '1px solid #4a4366',
      borderRadius: '5px',
      background: 'rgba(20,16,30,0.95)',
      color: '#fff',
      textAlign: 'center',
    });

    const country = document.createElement('select');
    Object.assign(country.style, {
      font: FONT,
      padding: '8px 6px',
      border: '1px solid #4a4366',
      borderRadius: '5px',
      background: 'rgba(20,16,30,0.95)',
      color: '#fff',
    });
    country.appendChild(opt('', '🌐 Auto')); // default - server fills it from the player's location
    for (const [code, label] of COUNTRIES) country.appendChild(opt(code, `${flagEmoji(code)} ${label}`));
    row.append(name, country);
    slot.appendChild(row);

    const submit = document.createElement('button');
    submit.textContent = 'SUBMIT';
    Object.assign(submit.style, {
      font: FONT,
      padding: '8px 22px',
      border: '1px solid #5cc8ff',
      borderRadius: '5px',
      background: 'rgba(40,80,110,0.6)',
      color: '#fff',
      cursor: 'pointer',
    });
    slot.appendChild(submit);

    const status = document.createElement('div');
    status.style.marginTop = '10px';
    status.style.fontSize = '12px';
    slot.appendChild(status);

    let busy = false;
    submit.onclick = async (): Promise<void> => {
      if (busy) return;
      busy = true;
      submit.disabled = true;
      status.textContent = 'Submitting…';
      status.style.color = '#9a93b0';
      const res = await client.submit(name.value, stats.score, country.value, avatar, Math.round(stats.seconds * 1000));
      if (res.ok) {
        const chosen = name.value.trim() || 'ANON';
        if (this.shareData !== null) {
          this.shareData.rank = res.rank;
          this.shareData.name = chosen;
          this.shareData.avatar = avatar;
          this.shareData.country = country.value !== '' ? country.value : null;
        }
        slot.replaceChildren();
        slot.appendChild(heading(res.rank !== null ? `YOU'RE #${res.rank}!` : 'SUBMITTED!', '#9fffa0', '18px'));
        const board = renderLeaderboard((await client.fetchLeaderboard()).slice(0, 10), { name: chosen, score: stats.score });
        board.style.marginTop = '12px';
        slot.appendChild(board);
      } else {
        busy = false;
        submit.disabled = false;
        status.textContent = res.error === 'offline' || res.error === 'network' ? 'Leaderboard unreachable.' : 'Could not submit.';
        status.style.color = '#ff8a98';
      }
    };
  }

  private paintFocus(): void {
    this.focusables.forEach((b, i) => {
      b.style.boxShadow = i === this.focusIndex ? '0 0 0 3px #5cc8ff' : 'none';
    });
    this.focusables[this.focusIndex]?.focus({ preventScroll: true });
  }

  private startPadPoll(): void {
    const loop = (): void => {
      if (!this.visible) {
        this.padRaf = 0;
        return;
      }
      this.pollPad();
      this.padRaf = requestAnimationFrame(loop);
    };
    this.padRaf = requestAnimationFrame(loop);
  }

  // Gamepad on the game-over screen: d-pad/stick move focus between Share and Restart, A activates.
  private pollPad(): void {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let gp: Gamepad | null = null;
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (p !== null) {
        gp = p;
        break;
      }
    }
    if (gp === null) return;
    const ax = gp.axes[0] ?? 0;
    const left = (gp.buttons[14]?.pressed ?? false) || ax < -0.5;
    const right = (gp.buttons[15]?.pressed ?? false) || ax > 0.5;
    const a = gp.buttons[0]?.pressed ?? false;
    if (left || right || a) setInputMode('gamepad', looksXbox(gp.id));
    const n = this.focusables.length;
    if (n > 0) {
      if (left && !this.prevPad.left) {
        this.focusIndex = (this.focusIndex - 1 + n) % n;
        this.paintFocus();
      } else if (right && !this.prevPad.right) {
        this.focusIndex = (this.focusIndex + 1) % n;
        this.paintFocus();
      }
      if (a && !this.prevPad.a) this.focusables[this.focusIndex]?.click();
    }
    this.prevPad.left = left;
    this.prevPad.right = right;
    this.prevPad.a = a;
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (!this.visible) return;
    setInputMode('keyboard');
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
    const n = this.focusables.length;
    if (n === 0) return;
    if (e.code === 'ArrowLeft') {
      e.preventDefault();
      this.focusIndex = (this.focusIndex - 1 + n) % n;
      this.paintFocus();
    } else if (e.code === 'ArrowRight') {
      e.preventDefault();
      this.focusIndex = (this.focusIndex + 1) % n;
      this.paintFocus();
    }
  };
}

function victoryCrest(): HTMLCanvasElement {
  const pattern = [
    '..YYYYYYY..',
    '.YY.....YY.',
    'YYY.....YYY',
    'YYY.....YYY',
    '.YYY...YYY.',
    '..YYYYYYY..',
    '....YYY....',
    '...YYYYY...',
    '..YYYYYYY..',
  ];
  const scale = 4;
  const canvas = document.createElement('canvas');
  canvas.width = pattern[0].length * scale;
  canvas.height = pattern.length * scale;
  const context = canvas.getContext('2d');
  if (context !== null) {
    context.imageSmoothingEnabled = false;
    context.fillStyle = '#ffd34d';
    for (let y = 0; y < pattern.length; y++) {
      for (let x = 0; x < pattern[y].length; x++) {
        if (pattern[y][x] === 'Y') context.fillRect(x * scale, y * scale, scale, scale);
      }
    }
    context.fillStyle = '#fff1a6';
    context.fillRect(4 * scale, scale, scale, scale);
    context.fillRect(5 * scale, scale, scale, scale);
  }
  Object.assign(canvas.style, {
    width: `${canvas.width}px`,
    height: `${canvas.height}px`,
    margin: '6px auto 3px',
    display: 'block',
    imageRendering: 'pixelated',
    filter: 'drop-shadow(0 4px 0 #6b4a08)',
  });
  canvas.setAttribute('aria-hidden', 'true');
  return canvas;
}

function victoryStats(time: string, kills: number, level: number, compact: boolean): HTMLDivElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    margin: compact ? '9px 0 7px' : '12px 0 9px',
    padding: compact ? '7px 0' : '9px 0',
    borderTop: '1px solid #4a4366',
    borderBottom: '1px solid #4a4366',
  });
  const values = [['TIME', time], ['KILLS', kills.toLocaleString()], ['LEVEL', level.toString()]];
  values.forEach(([label, value], index) => {
    const item = document.createElement('div');
    if (index > 0) item.style.borderLeft = '1px solid #4a4366';
    const name = line(label, '#9a93b0', compact ? '9px' : '10px');
    name.style.letterSpacing = '1px';
    const result = line(value, index === 0 ? '#5cc8ff' : '#e8e4f0', compact ? '14px' : '16px');
    result.style.marginTop = '4px';
    item.append(name, result);
    row.appendChild(item);
  });
  return row;
}

function ensureVictoryStyles(): void {
  if (document.getElementById('brewmancer-victory-styles') !== null) return;
  const style = document.createElement('style');
  style.id = 'brewmancer-victory-styles';
  style.textContent = `
    @keyframes brewmancer-victory-burst {
      0% { opacity: 0; transform: translate3d(0, 0, 0) scale(0); }
      12% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
      100% { opacity: 0; transform: translate3d(var(--victory-x), var(--victory-y), 0) rotate(180deg); }
    }
    @keyframes brewmancer-victory-pop {
      0% { opacity: 0; transform: scale(0.72); }
      72% { opacity: 1; transform: scale(1.08); }
      100% { opacity: 1; transform: scale(1); }
    }
    .brewmancer-victory-pixel {
      position: absolute;
      display: block;
      image-rendering: pixelated;
      animation: brewmancer-victory-burst 1300ms steps(8, end) both;
    }
    .brewmancer-victory-title {
      animation: brewmancer-victory-pop 420ms steps(5, end) both;
    }
    @media (prefers-reduced-motion: reduce) {
      .brewmancer-victory-pixel { display: none; }
      .brewmancer-victory-title { animation: none; }
    }
  `;
  document.head.appendChild(style);
}

function heading(text: string, color: string, size = '26px'): HTMLDivElement {
  const d = document.createElement('div');
  d.textContent = text;
  Object.assign(d.style, { font: `${size} "Silkscreen", ui-monospace, monospace`, color, letterSpacing: '2px', marginBottom: '6px' });
  return d;
}

function line(text: string, color: string, size: string): HTMLDivElement {
  const d = document.createElement('div');
  d.textContent = text;
  Object.assign(d.style, { color, fontSize: size, margin: '2px 0' });
  return d;
}

function opt(value: string, label: string): HTMLOptionElement {
  const o = document.createElement('option');
  o.value = value;
  o.textContent = label;
  return o;
}

function actionButton(text: string, border: string, bg: string, color: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = text;
  Object.assign(b.style, {
    font: FONT,
    padding: '10px 22px',
    border: `2px solid ${border}`,
    borderRadius: '6px',
    background: bg,
    color,
    cursor: 'pointer',
    outline: 'none',
  });
  return b;
}
